import { kebabCase } from '@makemydeal/dr-common-utils';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as baseDealAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/baseDealAnalyticsActionCreators';
import * as compositeSelectors from '@makemydeal/dr-dash-store/selectors/compositeSelectors';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as offerActionTypes from '@makemydeal/dr-dash-store/actionTypes/offerActionTypes';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as paymentSelectors from '@makemydeal/dr-dash-store/selectors/payment';
import { SELL_RATE_FIELD_EDIT_META } from '@makemydeal/dr-dash-store/middleware/sellRateFailureRollbackMiddleware';
import { dealLockHooks, useUPQRequiredFieldsValidation } from '../../../hooks';
import { useFieldOverrideIndicator } from '../../../hooks/useFieldOverrideIndicator';
import { useUserTypedGuard } from '../../../hooks/useUserTypedGuard';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { KeyboardEventHandler } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    APR,
    APR_PERCENTAGE,
    GENERIC_LAST_FIELD_CHANGED_ERROR,
    MONEY_FACTOR,
    MONEY_FACTOR_TYPE,
    RATE,
    SELL_RATE,
    SELL_RATE_REQUIRED_ERROR
} from '../../../constants';
import { convertToNumber, formatAprRate, formatMoneyFactor, getConvertedRateText } from '../../../utils/formatUtils';
import type { OfferType } from '@makemydeal/dr-platform-types';
import { FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { DealTabs } from '@makemydeal/dr-shared-types';
import { useHeldSelector } from '../../../hooks/useHeldSelector';

const SELL_RATE_OVERRIDE_INDICATOR_TEST_ID = 'sell-rate-override-indicator';

export const useSellRateField = ({ tabName, onLenderSlideOutOpen }: { tabName?: DealTabs; onLenderSlideOutOpen?: () => void }) => {
    const isSummaryTab = tabName === DealTabs.SUMMARY;
    const isLenderTab = tabName === DealTabs.LENDER;
    const dispatch = useDispatch();

    const isRollToUpqOnFirstCalculationEnabled = useSelector(featureToggleSelectors.isRollToUpqOnFirstCalculationEnabled);
    const rateType = useSelector((state: StateTree) =>
        isRollToUpqOnFirstCalculationEnabled
            ? offerReduxSelectors.getRateType(state) || offerSelectors.getRateTypeOverrideFromOfferTypeOrSelectedTerm(state)
            : offerSelectors.getRateTypeOverrideFromOfferTypeOrSelectedTerm(state)
    );
    const currentTermMonths = useSelector(offerSelectors.getSelectedTermMonths);
    const failed = useSelector(compositeSelectors.getLastFailureActionIfError(offerActionTypes.UPDATED_SELL_RATE_OVERRIDE));
    const selectedTerm = useSelector(offerReduxSelectors.getSelectedTerm);
    const currentOfferType: OfferType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const months = useSelector(offerReduxSelectors.getSelectedTermMonths);
    const fieldName = kebabCase(SELL_RATE);
    const isFlexBuyPayment = useSelector(offerReduxSelectors.isFlexBuyPayment);
    const flexBuyPaymentGridEnabled = useSelector(offerReduxSelectors.getFlexBuyPaymentGridToggle);
    const paymentGridEnabledToggle = useSelector((state: StateTree) =>
        offerReduxSelectors.getPaymentGridToggle(state, currentOfferType)
    );
    const isPaymentGridEnabled = isFlexBuyPayment ? flexBuyPaymentGridEnabled : paymentGridEnabledToggle;
    const isLocked = dealLockHooks.useIsDealLocked();
    const value = useHeldSelector((state: StateTree) =>
        isRollToUpqOnFirstCalculationEnabled
            ? offerReduxSelectors.getSellRate(state)
            : offerSelectors.getSellRateOverrideWithFallback(state)
    );
    const storedRate = useSelector(offerSelectors.getSellRateOverrideWithFallback);
    const editBaseline = isRollToUpqOnFirstCalculationEnabled ? storedRate : value;
    const isUserProgramQuotes = useSelector(offerReduxSelectors.getIsUserProgramQuotes);
    const {
        enabled: overrideIndicatorEnabled,
        show: showOverrideIndicator,
        message: overrideMessage,
        recordManualOverride
    } = useFieldOverrideIndicator('sellRate');
    const { markTyped, recordIfUserEdited } = useUserTypedGuard(recordManualOverride);
    const { missingFields, hasMissingRequiredFields, lastPaymentMissingUPQFields } =
        useUPQRequiredFieldsValidation(currentOfferType);
    const isSellRateMissing = hasMissingRequiredFields && missingFields.some((f) => f.field === 'sellRate');
    const failedMessage = useMemo(() => {
        if (isSellRateMissing && (failed || lastPaymentMissingUPQFields)) return SELL_RATE_REQUIRED_ERROR;
        if (failed) return GENERIC_LAST_FIELD_CHANGED_ERROR;

        return undefined;
    }, [failed, isSellRateMissing, lastPaymentMissingUPQFields]);

    // determine formatter by rate type
    const formatter = rateType === MONEY_FACTOR_TYPE ? formatMoneyFactor : formatAprRate;
    const rateTypeLabel = rateType === MONEY_FACTOR_TYPE ? MONEY_FACTOR : APR;
    const isAprRateType = rateType === APR;
    const rateTypePrefix = isAprRateType ? APR_PERCENTAGE : MONEY_FACTOR;

    // grab the current, formatted value
    const formattedValue = useMemo(() => formatter(value), [value, formatter, rateType]);

    // setup state for the actual input; default with currently formatted state value
    const [inputText, setInputText] = useState(formattedValue);
    const isEditingRef = useRef(false);
    const isCalculatingPayment = useSelector(
        (state: StateTree) => isRollToUpqOnFirstCalculationEnabled && paymentSelectors.isCalculatingPayment(state)
    );

    // calculate converted rate for PQ display
    const convertedRateText = useMemo(() => getConvertedRateText(rateType, value, false), [value, rateType]);

    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);
    const isExplicitLenderP1Enabled = useSelector(featureToggleSelectors.isExplicitLenderP1Enabled);

    // action creator to also consider selected term months
    const actionCreator = (newValue: number) => {
        if (typeof currentTermMonths !== 'number') return;

        const action = offerReduxActionCreators.updateSellRateOverride(newValue, currentTermMonths);
        return isRollToUpqOnFirstCalculationEnabled
            ? { ...action, meta: { ...action.meta, [SELL_RATE_FIELD_EDIT_META]: true } }
            : action;
    };

    const handleInputChanged = (e: any) => {
        const newValue = convertToNumber(e.target.value);
        const prevValue = editBaseline;

        if (e.target.value === inputText || isNaN(newValue)) return;

        markTyped(e);
        isEditingRef.current = true;

        dispatch(offerActionCreators.updatePaymentCallPendingState(prevValue !== newValue));

        setInputText(e.target.value);
    };

    const handleInputBlur = () => {
        isEditingRef.current = false;
        const formattedInputText = formatter(convertToNumber(inputText));
        if (formattedInputText !== inputText) setInputText(formattedInputText);

        if (editBaseline !== convertToNumber(inputText)) {
            dispatch(actionCreator(convertToNumber(inputText)));
            recordIfUserEdited();
        }
    };

    const handleClick = () => {
        if (onLenderSlideOutOpen) {
            onLenderSlideOutOpen();
        } else {
            dispatch(baseDealActionCreators.handleTabChange('lender'));
            dispatch(baseDealAnalyticsActionCreators.lenderTabClicked());
        }
    };

    const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
        if (e.key === 'Enter') {
            isEditingRef.current = false;
            if (editBaseline !== convertToNumber(inputText)) {
                dispatch(actionCreator(convertToNumber(inputText)));
                recordIfUserEdited();
            }

            e.preventDefault();
        }
    };

    // NOTE: upstream change; i.e. term changed in redux causing new override to be set
    // TODO: future integration test here.
    // istanbul ignore next
    useEffect(() => {
        if (inputText === formattedValue) return undefined;
        setInputText(formattedValue);
        return undefined;
    }, [formattedValue]);

    useEffect(() => {
        if (!isRollToUpqOnFirstCalculationEnabled || isCalculatingPayment || isEditingRef.current) return;
        setInputText(formattedValue);
    }, [isCalculatingPayment]);

    useEffect(() => {
        if (isDealScreenRedDotErrorsEnabled && tabName) {
            const isPaymentError: boolean = failed;
            dispatch(dealScreenErrorActionCreators.setFieldError(tabName, fieldName, failed, isPaymentError));
        }
    }, [isDealScreenRedDotErrorsEnabled, dispatch, failed, tabName]);

    const handleTogglesClick = () => {
        const rateType = !isAprRateType ? APR : MONEY_FACTOR_TYPE;
        dispatch(baseDealActionCreators.toggleRateType(rateType, selectedTerm.id, currentOfferType, months));
    };
    const handleOfferType = () => {
        if (currentOfferType === FINANCE) dispatch(interactionAnalyticsActionCreators.financeSellRateTextInput());
        else if (currentOfferType === LEASE) dispatch(interactionAnalyticsActionCreators.leaseSellRateTextInput());
    };

    const isLeaseOffer = currentOfferType === 'lease';

    return {
        label: isLeaseOffer ? `${RATE}` : `${RATE} (${rateTypeLabel})`,
        isLeaseOffer,
        isSummaryTab,
        isLenderTab,
        isAprRateType,
        isLocked,
        isPaymentGridEnabled,
        isUserProgramQuotes,
        convertedRateText,
        onLabelActivate: handleClick,
        onRateTypeToggle: handleTogglesClick,
        overrideIndicatorProps: {
            enabled: overrideIndicatorEnabled,
            show: showOverrideIndicator,
            message: overrideMessage,
            testId: SELL_RATE_OVERRIDE_INDICATOR_TEST_ID,
            showTooltip: !isExplicitLenderP1Enabled
        },
        inputProps: {
            ...(isLeaseOffer && { inputPrefix: rateTypePrefix }),
            name: fieldName,
            onChange: handleInputChanged,
            onBlur: () => {
                handleInputBlur();
                handleOfferType();
            },
            onKeyDown: handleKeyDown,
            hasError: failed || isSellRateMissing,
            errorMessage: failedMessage,
            placeholder: formatter(0),
            value: inputText,
            disabled: isLocked
        }
    };
};
